import { useEffect, useState } from "react";
import { Video, Clock, ExternalLink, Calendar } from "lucide-react";
import { API } from "../App";

/** Compact "next class" widget. Variants:
 *   - dashboard: full-width card meant for the student dashboard
 *   - welcome:   matches the Welcome Packet "Your First Day" cosmic card
 *   - inline:    minimal pill suitable for course/cohort pages
 *
 * The button is ALWAYS visible. It is enabled only inside the class window
 * (15-min lead through end-of-class). Outside the window it shows a
 * countdown to the next session.
 */
const fetchEndpoint = async (endpoint) => {
  const res = await fetch(`${API}${endpoint}`, { credentials: "include" });
  if (!res.ok) throw new Error(`Failed: ${res.status}`);
  return res.json();
};

const formatDelta = (msUntil) => {
  if (msUntil <= 0) return "now";
  const totalMin = Math.floor(msUntil / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;
  if (days >= 1) return `in ${days}d ${hours}h`;
  if (hours >= 1) return `in ${hours}h ${mins}m`;
  return `in ${mins}m`;
};

const formatLocalTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const LiveClassWidget = ({ endpoint = "/me/live-class", variant = "dashboard" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // Live tick every 30s so the countdown stays accurate
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchEndpoint(endpoint)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpoint, tick === 0 ? 0 : Math.floor(tick / 20)]); // refetch every ~10 min

  if (loading) return null;
  if (!data || !data.window) return null;

  const { window: w, instructor, zoom_link, cohort } = data;
  const opensAt = w?.opens_at ? new Date(w.opens_at) : null;
  const startsAt = w?.starts_at ? new Date(w.starts_at) : null;
  const endsAt = w?.ends_at ? new Date(w.ends_at) : null;
  const now = new Date();
  const isOpen = opensAt && endsAt && now >= opensAt && now <= endsAt;
  const hasZoom = !!zoom_link;
  const status =
    !hasZoom ? "no_link"
    : isOpen ? "live"
    : (opensAt && now < opensAt) ? "upcoming"
    : "ended";

  const buttonClasses =
    "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 font-semibold transition shadow-sm";

  const enabled = status === "live";
  const buttonStyle = enabled
    ? "bg-brand-gold text-brand-navy hover:bg-brand-gold/90 ring-2 ring-brand-gold/40 animate-[pulse_2s_ease-in-out_infinite]"
    : "bg-slate-200 text-slate-400 cursor-not-allowed";

  const subline =
    status === "live"
      ? "Class is live right now."
      : status === "upcoming"
      ? `Next session ${formatDelta(opensAt - now)} (${formatLocalTime(startsAt)})`
      : status === "no_link"
      ? "Your instructor hasn't posted a join link yet."
      : "Today's class has ended. See you next session!";

  const ButtonInner = () => (
    <>
      <Video className="w-4 h-4" />
      {enabled ? "Join Live Class" : "Join Live Class"}
      {enabled && <ExternalLink className="w-3.5 h-3.5 opacity-80" />}
    </>
  );

  const Btn = () =>
    enabled ? (
      <a
        href={zoom_link}
        target="_blank"
        rel="noopener noreferrer"
        className={`${buttonClasses} ${buttonStyle}`}
        data-testid="join-live-class-btn"
        aria-label="Join Live Class"
      >
        <ButtonInner />
      </a>
    ) : (
      <button
        type="button"
        disabled
        className={`${buttonClasses} ${buttonStyle}`}
        data-testid="join-live-class-btn-disabled"
        aria-disabled="true"
        title={subline}
      >
        <ButtonInner />
      </button>
    );

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-3" data-testid="live-class-widget-inline">
        <Btn />
        <span className="text-xs text-slate-500">{subline}</span>
      </div>
    );
  }

  if (variant === "welcome") {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-5" data-testid="live-class-widget-welcome">
        <Btn />
        <div className="text-sm text-white/80">
          <p className="m-0">{subline}</p>
          {w?.schedule_label && (
            <p className="text-xs text-white/60 mt-0.5 m-0">Class hours: {w.schedule_label}</p>
          )}
        </div>
      </div>
    );
  }

  // dashboard (default)
  return (
    <div
      className="rounded-2xl border-2 border-brand-gold/30 bg-gradient-to-br from-brand-navy via-[#152252] to-[#0f1b3d] text-white p-5 sm:p-6 shadow-md"
      data-testid="live-class-widget-dashboard"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-brand-gold/20 flex items-center justify-center shrink-0">
          <Video className="w-6 h-6 text-brand-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-1">
            Live Class
          </p>
          <p className="text-lg font-heading font-bold truncate">
            {data.program?.name || "Your Program"}
            {instructor?.name ? <span className="text-white/70 font-normal"> · with {instructor.name}</span> : null}
          </p>
          <p className="text-sm text-white/80 mt-0.5 flex items-center gap-2">
            {status === "live" ? <Clock className="w-3.5 h-3.5 text-brand-gold" /> : <Calendar className="w-3.5 h-3.5" />}
            {subline}
          </p>
        </div>
        <div className="shrink-0">
          <Btn />
        </div>
      </div>
    </div>
  );
};

export default LiveClassWidget;
